import React, { useState, useEffect, useRef, useContext, useCallback } from "react";

import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import { makeStyles } from "@material-ui/core/styles";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { Tooltip, Avatar } from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";

import GroupIcon from '@material-ui/icons/Group';
import ContactTag from "../ContactTag";
import ConnectionIcon from "../ConnectionIcon";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ShowTicketOpen from "../ShowTicketOpenModal";
import { isNil } from "lodash";
import { toast } from "react-toastify";
import { Done, HighlightOff, Replay, SwapHoriz } from "@material-ui/icons";
import useCompanySettings from "../../hooks/useSettings/companySettings";

const useStyles = makeStyles((theme) => ({
    item: {
        position: "relative",
        display: "flex",
        alignItems: "center",
        padding: "10px 12px 10px 14px",
        cursor: "pointer",
        borderBottom: "1px solid " + (theme.mode === "light" ? "rgba(226,232,240,0.7)" : "rgba(51,65,85,0.55)"),
        backgroundColor: "transparent",
        transition: "background-color 0.15s ease",
        "&:hover": {
            backgroundColor: theme.mode === "light" ? "rgba(241,245,249,0.85)" : "rgba(30,41,59,0.65)",
        },
        "&:last-child": {
            borderBottom: "none",
        },
    },
    itemSelected: {
        backgroundColor: theme.mode === "light" ? "rgba(219,234,254,0.6)" : "rgba(30,58,138,0.22)",
        borderLeft: "3px solid " + (theme.mode === "light" ? "#2563eb" : "#60a5fa"),
        "&:hover": {
            backgroundColor: theme.mode === "light" ? "rgba(219,234,254,0.75)" : "rgba(30,58,138,0.3)",
        },
    },
    itemUnread: {
        borderLeft: "3px solid " + (theme.mode === "light" ? "#22c55e" : "#4ade80"),
    },
    itemPending: {
        cursor: "default",
    },
    avatarWrap: {
        position: "relative",
        marginRight: 10,
        flexShrink: 0,
    },
    avatar: {
        width: 48,
        height: 48,
        borderRadius: "50%",
        border: "2px solid " + (theme.mode === "light" ? "rgba(226,232,240,0.9)" : "rgba(51,65,85,0.9)"),
    },
    channelDot: {
        position: "absolute",
        bottom: 0,
        right: 0,
        width: 18,
        height: 18,
        borderRadius: "50%",
        backgroundColor: "#25D366",
        border: "2px solid " + (theme.mode === "light" ? "#fff" : "#0f172a"),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    content: {
        flex: 1,
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: 2,
    },
    topRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 6,
    },
    name: {
        fontWeight: 700,
        fontSize: "0.88rem",
        color: theme.mode === "light" ? "#0f172a" : "#f8fafc",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        flex: 1,
    },
    nameUnread: {
        color: theme.mode === "light" ? "#0f172a" : "#f1f5f9",
    },
    timeWrap: {
        display: "flex",
        alignItems: "center",
        gap: 4,
        flexShrink: 0,
    },
    time: {
        fontSize: "0.68rem",
        color: theme.mode === "light" ? "#94a3b8" : "#64748b",
        fontWeight: 500,
    },
    timeUnread: {
        color: theme.mode === "light" ? "#16a34a" : "#4ade80",
        fontWeight: 700,
    },
    unreadBubble: {
        minWidth: 18,
        height: 18,
        borderRadius: 9,
        padding: "0 5px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "0.62rem",
        fontWeight: 700,
        color: "#fff",
        background: "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)",
        boxShadow: "0 2px 6px rgba(22,163,74,0.4)",
    },
    preview: {
        fontSize: "0.76rem",
        color: theme.mode === "light" ? "#64748b" : "#94a3b8",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        fontWeight: 400,
        lineHeight: 1.4,
    },
    previewUnread: {
        fontWeight: 600,
        color: theme.mode === "light" ? "#334155" : "#cbd5e1",
    },
    pillsRow: {
        display: "flex",
        alignItems: "center",
        gap: 4,
        flexWrap: "wrap",
        marginTop: 2,
    },
    pill: {
        fontSize: "0.58rem",
        fontWeight: 700,
        color: "#fff",
        borderRadius: 999,
        padding: "1px 7px",
        letterSpacing: "0.02em",
        maxWidth: 100,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
    },
    tagsRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: 3,
        marginTop: 2,
    },
    actionsRow: {
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 4,
        marginLeft: 8,
        flexShrink: 0,
    },
    actionBtnsWrap: {
        display: "flex",
        alignItems: "center",
        gap: 3,
    },
    actionBtn: {
        width: 26,
        height: 26,
        borderRadius: "50%",
        minWidth: 26,
        padding: 0,
        fontSize: "0.7rem",
        transition: "transform 0.15s ease",
        "&:hover": { transform: "scale(1.1)" },
    },
    btnAccept: {
        background: "linear-gradient(135deg, #22c55e, #16a34a)",
        color: "#fff",
    },
    btnTransfer: {
        background: "linear-gradient(135deg, #3b82f6, #2563eb)",
        color: "#fff",
    },
    btnClose: {
        background: "linear-gradient(135deg, #f97316, #ea580c)",
        color: "#fff",
    },
    btnNeutral: {
        background: "linear-gradient(135deg, #64748b, #475569)",
        color: "#fff",
    },
}));

const TicketListItemCustom = ({ setTabOpen, ticket }) => {
    const classes = useStyles();
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [acceptTicketWithouSelectQueueOpen, setAcceptTicketWithouSelectQueueOpen] = useState(false);
    const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
    const [openAlert, setOpenAlert] = useState(false);
    const [userTicketOpen, setUserTicketOpen] = useState("");
    const [queueTicketOpen, setQueueTicketOpen] = useState("");

    const { ticketId } = useParams();
    const isMounted = useRef(true);
    const { setCurrentTicket } = useContext(TicketsContext);
    const { user } = useContext(AuthContext);
    const { get: getSetting } = useCompanySettings();

    useEffect(() => {
        return () => { isMounted.current = false; };
    }, []);

    const handleOpenAcceptTicketWithouSelectQueue = useCallback(() => {
        setAcceptTicketWithouSelectQueueOpen(true);
    }, []);

    const handleCloseTicket = async (id) => {
        const setting = await getSetting({ "column": "requiredTag" });
        if (setting.requiredTag === "enabled") {
            try {
                const contactTags = await api.get(`/contactTags/${ticket.contact.id}`);
                if (!contactTags.data.tags) {
                    toast.warning(i18n.t("messagesList.header.buttons.requiredTag"));
                } else {
                    await api.put(`/tickets/${id}`, { status: "closed", userId: user?.id || null });
                    if (isMounted.current) setLoading(false);
                    history.push(`/tickets/`);
                }
            } catch (err) {
                setLoading(false);
                toastError(err);
            }
        } else {
            setLoading(true);
            try {
                await api.put(`/tickets/${id}`, { status: "closed", userId: user?.id || null });
            } catch (err) {
                setLoading(false);
                toastError(err);
            }
            if (isMounted.current) setLoading(false);
            history.push(`/tickets/`);
        }
    };

    const handleCloseIgnoreTicket = async (id) => {
        setLoading(true);
        try {
            await api.put(`/tickets/${id}`, {
                status: "closed",
                userId: user?.id || null,
                sendFarewellMessage: false,
                amountUsedBotQueues: 0
            });
        } catch (err) {
            setLoading(false);
            toastError(err);
        }
        if (isMounted.current) setLoading(false);
        history.push(`/tickets/`);
    };

    const truncate = (str, len) => {
        if (!isNil(str)) {
            if (str.length > len) return str.substring(0, len) + "...";
            return str;
        }
    };

    const handleCloseTransferTicketModal = useCallback(() => {
        if (isMounted.current) setTransferTicketModalOpen(false);
    }, []);

    const handleOpenTransferModal = () => {
        setLoading(true);
        setTransferTicketModalOpen(true);
        if (isMounted.current) setLoading(false);
        handleSelectTicket(ticket);
        history.push(`/tickets/${ticket.uuid}`);
    };

    const handleAcepptTicket = async (id) => {
        setLoading(true);
        try {
            const otherTicket = await api.put(`/tickets/${id}`, ({
                status: ticket.isGroup && ticket.channel === "whatsapp" ? "group" : "open",
                userId: user?.id,
            }));
            if (otherTicket.data.id !== ticket.id) {
                if (otherTicket.data.userId !== user?.id) {
                    setOpenAlert(true);
                    setUserTicketOpen(otherTicket.data.user.name);
                    setQueueTicketOpen(otherTicket.data.queue.name);
                } else {
                    setLoading(false);
                    setTabOpen(ticket.isGroup ? "group" : "open");
                    handleSelectTicket(otherTicket.data);
                    history.push(`/tickets/${otherTicket.uuid}`);
                }
            } else {
                let setting;
                try {
                    setting = await getSetting({ "column": "sendGreetingAccepted" });
                } catch (err) { toastError(err); }
                if (setting.sendGreetingAccepted === "enabled" && (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled")) {
                    handleSendMessage(ticket.id);
                }
                if (isMounted.current) setLoading(false);
                setTabOpen(ticket.isGroup ? "group" : "open");
                handleSelectTicket(ticket);
                history.push(`/tickets/${ticket.uuid}`);
            }
        } catch (err) {
            setLoading(false);
            toastError(err);
        }
    };

    const handleSendMessage = async (id) => {
        let setting;
        try {
            setting = await getSetting({ "column": "greetingAcceptedMessage" });
        } catch (err) { toastError(err); }
        const msg = `${setting.greetingAcceptedMessage}`;
        const message = { read: 1, fromMe: true, mediaUrl: "", body: `${msg.trim()}` };
        try {
            await api.post(`/messages/${id}`, message);
        } catch (err) { toastError(err); }
    };

    const handleCloseAlert = useCallback(() => {
        setOpenAlert(false);
        setLoading(false);
    }, []);

    const handleSelectTicket = (ticket) => {
        const code = uuidv4();
        const { id, uuid } = ticket;
        setCurrentTicket({ id, uuid, code });
    };

    const isUnread = Number(ticket.unreadMessages) > 0;
    const isSelected = ticketId && ticketId === ticket.uuid;

    const formattedTime = ticket.lastMessage
        ? (isSameDay(parseISO(ticket.updatedAt), new Date())
            ? format(parseISO(ticket.updatedAt), "HH:mm")
            : format(parseISO(ticket.updatedAt), "dd/MM"))
        : "";

    const renderPreview = () => {
        if (!ticket.lastMessage) return "Sem mensagens";
        if (ticket.lastMessage.includes("fb.me")) return "📢 Clique de anúncio";
        if (ticket.lastMessage.includes("data:image/png;base64")) return "📍 Localização";
        if (ticket.lastMessage.includes("BEGIN:VCARD")) return "👤 Contato";
        return truncate(ticket.lastMessage, 60);
    };

    const getChannelColor = () => {
        if (ticket.channel === "facebook") return "#1877F2";
        if (ticket.channel === "instagram") return "#E1306C";
        return "#25D366";
    };

    return (
        <React.Fragment key={ticket.id}>
            {openAlert && (
                <ShowTicketOpen
                    isOpen={openAlert}
                    handleClose={handleCloseAlert}
                    user={userTicketOpen}
                    queue={queueTicketOpen}
                />
            )}
            {acceptTicketWithouSelectQueueOpen && (
                <AcceptTicketWithouSelectQueue
                    modalOpen={acceptTicketWithouSelectQueueOpen}
                    onClose={() => setAcceptTicketWithouSelectQueueOpen(false)}
                    ticketId={ticket.id}
                    ticket={ticket}
                />
            )}
            {transferTicketModalOpen && (
                <TransferTicketModalCustom
                    modalOpen={transferTicketModalOpen}
                    onClose={handleCloseTransferTicketModal}
                    ticketid={ticket.id}
                    ticket={ticket}
                />
            )}

            <div
                className={clsx(classes.item, {
                    [classes.itemSelected]: isSelected,
                    [classes.itemUnread]: isUnread && !isSelected,
                    [classes.itemPending]: ticket.status === "pending",
                })}
                onClick={(e) => {
                    const tag = e.target.tagName.toLowerCase();
                    if (tag === "button" || tag === "svg" || tag === "path") return;
                    handleSelectTicket(ticket);
                }}
            >
                {/* Avatar com ícone do canal */}
                <div className={classes.avatarWrap}>
                    <Avatar
                        className={classes.avatar}
                        src={ticket?.contact?.urlPicture}
                        alt={ticket?.contact?.name}
                    />
                    <div className={classes.channelDot} style={{ backgroundColor: getChannelColor() }}>
                        <ConnectionIcon
                            width="10"
                            height="10"
                            connectionType={ticket.channel}
                        />
                    </div>
                </div>

                {/* Conteúdo central */}
                <div className={classes.content}>
                    <div className={classes.topRow}>
                        <span className={clsx(classes.name, { [classes.nameUnread]: isUnread })}>
                            {ticket.isGroup && <GroupIcon style={{ fontSize: 13, verticalAlign: "middle", marginRight: 3 }} />}
                            {truncate(ticket.contact?.name, 32)}
                        </span>
                        <div className={classes.timeWrap}>
                            {isUnread && (
                                <span className={classes.unreadBubble}>{ticket.unreadMessages}</span>
                            )}
                            {formattedTime && (
                                <span className={clsx(classes.time, { [classes.timeUnread]: isUnread })}>
                                    {formattedTime}
                                </span>
                            )}
                        </div>
                    </div>

                    <span className={clsx(classes.preview, { [classes.previewUnread]: isUnread })}>
                        {renderPreview()}
                    </span>

                    <div className={classes.pillsRow}>
                        {ticket?.whatsapp && (
                            <span
                                className={classes.pill}
                                style={{ backgroundColor: getChannelColor() }}
                            >
                                {ticket.whatsapp?.name?.toUpperCase()}
                            </span>
                        )}
                        <span
                            className={classes.pill}
                            style={{ backgroundColor: ticket.queue?.color || "#64748b" }}
                        >
                            {ticket.queueId
                                ? ticket.queue?.name?.toUpperCase()
                                : ticket.status === "lgpd"
                                    ? "LGPD"
                                    : "SEM FILA"}
                        </span>
                        {ticket?.user && (
                            <span className={classes.pill} style={{ backgroundColor: "#334155" }}>
                                {ticket.user?.name?.split(" ")[0]?.toUpperCase()}
                            </span>
                        )}
                    </div>

                    {ticket.tags?.length > 0 && (
                        <div className={classes.tagsRow}>
                            {ticket.tags.map((tag) => (
                                <ContactTag tag={tag} key={`tag-${ticket.id}-${tag.id}`} />
                            ))}
                        </div>
                    )}
                </div>

                {/* Botões de ação */}
                <div className={classes.actionsRow}>
                    <div className={classes.actionBtnsWrap}>
                        {ticket.status === "pending" && (ticket.queueId === null || ticket.queueId === undefined) && (
                            <ButtonWithSpinner
                                variant="contained"
                                className={clsx(classes.actionBtn, classes.btnAccept)}
                                size="small"
                                loading={loading}
                                onClick={(e) => { e.stopPropagation(); handleOpenAcceptTicketWithouSelectQueue(); }}
                            >
                                <Tooltip title="Aceitar"><Done style={{ fontSize: 14 }} /></Tooltip>
                            </ButtonWithSpinner>
                        )}
                        {ticket.status === "pending" && ticket.queueId !== null && (
                            <ButtonWithSpinner
                                variant="contained"
                                className={clsx(classes.actionBtn, classes.btnAccept)}
                                size="small"
                                loading={loading}
                                onClick={(e) => { e.stopPropagation(); handleAcepptTicket(ticket.id); }}
                            >
                                <Tooltip title="Aceitar"><Done style={{ fontSize: 14 }} /></Tooltip>
                            </ButtonWithSpinner>
                        )}
                        {(ticket.status === "pending" || ticket.status === "open" || ticket.status === "group") && (
                            <ButtonWithSpinner
                                variant="contained"
                                className={clsx(classes.actionBtn, classes.btnTransfer)}
                                size="small"
                                loading={loading}
                                onClick={(e) => { e.stopPropagation(); handleOpenTransferModal(); }}
                            >
                                <Tooltip title="Transferir"><SwapHoriz style={{ fontSize: 14 }} /></Tooltip>
                            </ButtonWithSpinner>
                        )}
                        {(ticket.status === "open" || ticket.status === "group") && (
                            <ButtonWithSpinner
                                variant="contained"
                                className={clsx(classes.actionBtn, classes.btnClose)}
                                size="small"
                                loading={loading}
                                onClick={(e) => { e.stopPropagation(); handleCloseTicket(ticket.id); }}
                            >
                                <Tooltip title="Encerrar"><HighlightOff style={{ fontSize: 14 }} /></Tooltip>
                            </ButtonWithSpinner>
                        )}
                        {(ticket.status === "pending" || ticket.status === "lgpd") && (user.userClosePendingTicket === "enabled" || user.profile === "admin") && (
                            <ButtonWithSpinner
                                variant="contained"
                                className={clsx(classes.actionBtn, classes.btnClose)}
                                size="small"
                                loading={loading}
                                onClick={(e) => { e.stopPropagation(); handleCloseIgnoreTicket(ticket.id); }}
                            >
                                <Tooltip title="Ignorar"><HighlightOff style={{ fontSize: 14 }} /></Tooltip>
                            </ButtonWithSpinner>
                        )}
                        {ticket.status === "closed" && (ticket.queueId === null || ticket.queueId === undefined) && (
                            <ButtonWithSpinner
                                variant="contained"
                                className={clsx(classes.actionBtn, classes.btnNeutral)}
                                size="small"
                                loading={loading}
                                onClick={(e) => { e.stopPropagation(); handleOpenAcceptTicketWithouSelectQueue(); }}
                            >
                                <Tooltip title="Reabrir"><Replay style={{ fontSize: 14 }} /></Tooltip>
                            </ButtonWithSpinner>
                        )}
                        {ticket.status === "closed" && ticket.queueId !== null && (
                            <ButtonWithSpinner
                                variant="contained"
                                className={clsx(classes.actionBtn, classes.btnNeutral)}
                                size="small"
                                loading={loading}
                                onClick={(e) => { e.stopPropagation(); handleAcepptTicket(ticket.id); }}
                            >
                                <Tooltip title="Reabrir"><Replay style={{ fontSize: 14 }} /></Tooltip>
                            </ButtonWithSpinner>
                        )}
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
};

export default TicketListItemCustom;
